import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/PasswordInput';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { Mail, Lock, User, Phone, UserPlus, Building2 } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleAuth = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const Register = () => {
  const { t, dir, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company_name: '', password: '' });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast({ title: t('auth.welcome_back') });
      navigate('/Dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || (lang === 'ar' ? 'تعذر إنشاء الحساب' : 'Registration failed');
      toast({ title: detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center noise-bg" dir={dir}>
      <div className="absolute inset-0 spot-light pointer-events-none" />
      <div className="max-w-md w-full mx-auto px-6 relative">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl md:text-5xl text-[#0a0a0a]">{t('auth.register_title')}</h1>
          <p className="mt-2 text-sm text-[#111827]/60">{t('auth.register_sub')}</p>
        </div>

        <div className="glass-card rounded-2xl p-8">
          <button onClick={googleAuth} className="w-full flex items-center justify-center gap-3 h-12 rounded-md bg-white text-[#0a0a0a] font-medium text-sm border border-[#e5e7eb] hover:border-[#2563eb] hover:bg-[#f8fafc] hover:shadow-sm transition-all">
            <GoogleIcon />
            {t('auth.continue_google')}
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#2563eb]/15" />
            <span className="text-xs uppercase tracking-widest text-[#111827]/40">{t('auth.or')}</span>
            <div className="flex-1 h-px bg-[#2563eb]/15" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('auth.name')}</Label>
              <div className="relative">
                <User className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                <Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827] focus-visible:ring-[#2563eb]/40" />
              </div>
            </div>
            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                <Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
              </div>
            </div>
            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('auth.phone')}</Label>
              <div className="relative">
                <Phone className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+971 ..." className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" data-testid="register-phone" />
              </div>
            </div>
            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">
                {lang === 'ar' ? 'اسم الشركة' : 'Company name'} <span className="text-[10px] normal-case tracking-normal text-[#111827]/40 lowercase">({lang === 'ar' ? 'اختياري' : 'optional'})</span>
              </Label>
              <div className="relative">
                <Building2 className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder={lang === 'ar' ? 'مثلاً: iAccounting FZ-LLC' : 'e.g. iAccounting FZ-LLC'}
                  className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]"
                  data-testid="register-company"
                />
              </div>
              <div className="mt-1 text-[10px] text-[#111827]/50">
                {lang === 'ar' ? 'يمكنك تعبئته لاحقاً — يُستخدم في دفاتر AI Accounting والإيصالات' : 'You can fill this later — used across your AI Accounting books and receipts'}
              </div>
            </div>
            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                <PasswordInput required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="gold-button rounded-md h-12 w-full text-sm">
              {loading ? t('auth.creating') : <><UserPlus className="w-4 h-4 me-2" /> {t('auth.register')}</>}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-[#111827]/60">
            {t('auth.have_account')} <Link to="/login" className="text-[#3b82f6] hover:text-[#0a0a0a]">{t('auth.sign_in_here')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
